import { useState, useEffect } from 'react';
import { Search, Calendar, Tag, ArrowRight, Shield } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import BottomCTA from '@/components/BottomCTA';
import Link from 'next/link';
import { useRouter } from 'next/router';
import SEO from '../../components/SEO';
import { client } from '../../tina/__generated__/client';
import Image from 'next/image';

// Create a mapping of icon names to their components
const getIconComponent = (iconName) => {
    // Convert the icon name to PascalCase if it's not already
    const pascalCaseName = iconName
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join('');

    // Get the icon component from LucideIcons
    return LucideIcons[pascalCaseName] || LucideIcons.HelpCircle; // Fallback to HelpCircle if icon not found
};

// Helper function to convert category name to slug
const getCategorySlug = (categoryName) => {
    const slugMap = {
        'eIDAS 2.0 Explained': 'eidas-explained',
        'Getting Started': 'getting-started',
        'Business Applications': 'business-applications',
        'Security & Privacy': 'security-and-privacy',
        'Use Cases': 'use-cases'
    };
    return slugMap[categoryName]// || categoryName.toLowerCase().replace(/\s+/g, '-');
};

// Function to truncate text to 2 sentences
const truncateToTwoSentences = (text) => {
    if (!text) return '';
    const sentences = text.match(/[^.!?]+[.!?]+/g) || [];
    return sentences.slice(0, 2).join(' ').trim();
};

export default function Blog({ categories = [], posts = [] }) {
    const router = useRouter();
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');

    // Update selected category when URL changes
    useEffect(() => {
        const category = router.query.category;
        if (category) {
            setSelectedCategory(category);
        }
    }, [router.query.category]);

    const handleCategoryChange = (category) => {
        setSelectedCategory(category);
        // Update URL without page refresh
        router.push({
            pathname: router.pathname,
            query: category === 'all' ? {} : { category }
        }, undefined, { shallow: true });
    };

    const filteredPosts = posts?.filter(post => {
        const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            post.content?.children?.[0]?.children?.[0]?.text.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = selectedCategory === 'all' || post.category?.name === selectedCategory;
        return matchesSearch && matchesCategory;
    }) || [];

    // Group posts by category
    const postsByCategory = categories.reduce((acc, category) => {
        acc[category.name] = filteredPosts.filter(post => post.category?.name === category.name);
        return acc;
    }, {});

    // Get total post count for each category
    const categoryPostCounts = categories.reduce((acc, category) => {
        acc[category.name] = posts.filter(post => post.category?.name === category.name).length;
        return acc;
    }, {});

    // Sort categories by post count
    const sortedCategories = [...categories].sort((a, b) => {
        return categoryPostCounts[b.name] - categoryPostCounts[a.name];
    });

    return (
        <div className="min-h-screen bg-white">
            <SEO
                title="BusinessWallet.eu Blogs - Digital Identity & Business Insights"
                description="Explore our comprehensive guides and insights on digital identity, eIDAS, and business transformation. Stay informed about the latest trends in digital identity management."
                keywords="business wallet blog, digital identity, eidas compliance, business transformation, digital identity management, business insights"
                ogUrl='https://businesswallet.eu/blog'
            />
            <Header />
            <main>
                {/* Hero Section */}
                <section className="relative bg-gradient-to-br from-slate-900 to-slate-800">
                    <div className="absolute inset-0 bg-[url('/grid.svg')] bg-center [mask-image:linear-gradient(180deg,white,rgba(255,255,255,0))]"></div>

                    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                        <div className="text-center max-w-3xl mx-auto">
                            <div className="inline-flex items-center space-x-3 bg-white/10 px-4 py-2 rounded-full mb-8">
                                <Shield className="w-5 h-5 text-sky-400" />
                                <span className="text-sm font-medium text-sky-400">Knowledge Center</span>
                            </div>

                            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                                Digital Identity & Business Insights
                            </h1>

                            <p className="text-xl text-slate-300 mb-12">
                                Explore our comprehensive guides and insights on digital identity, eIDAS, and business transformation.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Main Content */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                    <div className="grid lg:grid-cols-12 gap-12">
                        {/* Sidebar */}
                        <div className="lg:col-span-3">
                            <div className="sticky top-8">
                                {/* Search Bar */}
                                <div className="relative w-full mb-8">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                                    <input
                                        type="text"
                                        placeholder="Search articles..."
                                        className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-sky-500 focus:outline-none"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                </div>

                                {/* Categories */}
                                <div className="mb-8">
                                    <h2 className="text-lg font-semibold text-slate-900 mb-4">Categories</h2>
                                    <div className="space-y-2">
                                        <button
                                            onClick={() => handleCategoryChange('all')}
                                            className={`w-full flex items-center justify-between p-3 rounded-lg text-left transition-colors ${selectedCategory === 'all'
                                                ? 'bg-sky-50 text-sky-600'
                                                : 'text-slate-600 hover:bg-slate-50'
                                                }`}
                                        >
                                            <span className="font-medium">All Categories</span>
                                            <span className="text-sm bg-slate-100 px-2 py-1 rounded-full">
                                                {posts.length}
                                            </span>
                                        </button>
                                        {sortedCategories?.length > 0 && sortedCategories.map((category) => {
                                            const Icon = getIconComponent(category.icon);
                                            return (
                                                <Link
                                                    key={category.name}
                                                    href={`/blog/${getCategorySlug(category.name)}`}
                                                    className={`w-full flex items-center justify-between p-3 rounded-lg text-left transition-colors ${selectedCategory === category.name
                                                        ? 'bg-sky-50 text-sky-600'
                                                        : 'text-slate-600 hover:bg-slate-50'
                                                        }`}
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <Icon className="w-5 h-5" />
                                                        <span className="font-medium">{category.name}</span>
                                                    </div>
                                                    <span className="text-sm bg-slate-100 px-2 py-1 rounded-full">
                                                        {categoryPostCounts[category.name] || 0}
                                                    </span>
                                                </Link>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Articles Grid */}
                        <div className="lg:col-span-9">
                            {selectedCategory === 'all' ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    {filteredPosts
                                        .sort((a, b) => new Date(b.date) - new Date(a.date))
                                        .map((post) => {
                                            let text;

                                            text = post.content?.children?.[0]?.children?.map(child => {
                                                if (child.type === 'text') {
                                                    return child?.text;
                                                }
                                                if (child.type === 'a') {
                                                    return child?.children[0]?.text;
                                                }
                                                return '';
                                            }).join('');

                                            const ogImageUrl = `https://businesswallet.eu/api/blog/image/${post.slug}`;
                                            const mainImageUrl = post?.mainImage ? post?.mainImage : ogImageUrl;
                                            return (
                                                <Link
                                                    href={`/blog/${getCategorySlug(post.category?.name)}/${post.slug}`}
                                                    key={post.slug}
                                                    className="block hover:shadow-md transition-shadow"
                                                >
                                                    <article className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex flex-col h-full">
                                                        <div className="aspect-w-16 aspect-h-9">
                                                            <Image
                                                                width={300}
                                                                height={300}
                                                                src={mainImageUrl}
                                                                alt={post.title}
                                                                className="w-full aspect-video object-cover"
                                                            />
                                                        </div>
                                                        <div className="p-6 flex flex-col flex-grow">
                                                            <div className="flex flex-col items-start gap-4 text-sm text-slate-500 mb-4">
                                                                <div className="flex w-full justify-between items-center gap-4">
                                                                    <div className="flex items-center gap-1">
                                                                        <Calendar className="w-4 h-4" />
                                                                        <span>{new Date(post.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                                                                    </div>
                                                                    <span>{post.readTime}</span>
                                                                </div>
                                                            </div>
                                                            <h3 className="text-xl font-bold text-slate-900 mb-3">{post.title}</h3>
                                                            <p className="text-slate-600 mb-4 flex-grow line-clamp-3 max-h-[72px] overflow-hidden">
                                                                {text}
                                                            </p>
                                                            <div className="inline-flex items-center text-sky-500 font-medium hover:text-sky-600 mt-auto">
                                                                Read more
                                                                <ArrowRight className="w-4 h-4 ml-1" />
                                                            </div>
                                                        </div>
                                                    </article>
                                                </Link>
                                            );
                                        })}
                                </div>
                            ) : (
                                Object.entries(postsByCategory).map(([categoryName, categoryPosts]) => (
                                    categoryPosts.length > 0 && (
                                        <div key={categoryName} className="mb-12">
                                            <h2 className="text-2xl font-bold text-slate-900 mb-6">{categoryName}</h2>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                                {categoryPosts
                                                    .sort((a, b) => new Date(b.date) - new Date(a.date))
                                                    .map((post) => {
                                                        const ogImageUrl = `https://businesswallet.eu/api/blog/image/${post.slug}`;
                                                        const mainImageUrl = post?.mainImage ? post?.mainImage : ogImageUrl;
                                                        return (
                                                            <Link
                                                                href={`/blog/${getCategorySlug(post.category?.name)}/${post.slug}`}
                                                                key={post.slug}
                                                                className="block hover:shadow-md transition-shadow"
                                                            >
                                                                <article className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex flex-col h-full">
                                                                    <div className="aspect-w-16 aspect-h-9">
                                                                        <Image
                                                                            width={300}
                                                                            height={300}
                                                                            src={mainImageUrl}
                                                                            alt={post.title}
                                                                            className="w-full aspect-video object-cover"
                                                                        />
                                                                    </div>
                                                                    <div className="p-6 flex flex-col flex-grow">
                                                                        <div className="flex flex-col items-start gap-4 text-sm text-slate-500 mb-4">
                                                                            <div className="flex w-full justify-between items-center gap-4">
                                                                                <div className="flex items-center gap-1">
                                                                                    <Calendar className="w-4 h-4" />
                                                                                    <span>{new Date(post.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                                                                                </div>
                                                                                <span>{post.readTime}</span>
                                                                            </div>
                                                                        </div>
                                                                        <h3 className="text-xl font-bold text-slate-900 mb-3">{post.title}</h3>
                                                                        <p className="text-slate-600 mb-4 flex-grow line-clamp-3">
                                                                            {post.content?.children?.[0]?.children?.[0]?.text}
                                                                        </p>
                                                                        <div className="inline-flex items-center text-sky-500 font-medium hover:text-sky-600 mt-auto">
                                                                            Read more
                                                                            <ArrowRight className="w-4 h-4 ml-1" />
                                                                        </div>
                                                                    </div>
                                                                </article>
                                                            </Link>
                                                        );
                                                    })}
                                            </div>
                                        </div>
                                    )
                                ))
                            )}
                        </div>
                    </div>
                </div>

                {/* CTA Section */}
                <BottomCTA />
            </main>
            <Footer />
        </div>
    );
}

export async function getStaticProps() {
    try {
        // Fetch categories using the proper Tina client method
        let categoriesResponse = await client.queries.blogCategoryConnection({ first: 100 });
        let categories = categoriesResponse.data.blogCategoryConnection.edges.map(edge => edge.node);

        let postsResponse = await client.queries.blogConnection({ first: 100 });
        let posts = postsResponse.data.blogConnection.edges.map(edge => edge.node);

        return {
            props: {
                categories,
                posts
            },
            revalidate: 60, // Revalidate every minute
        };
    } catch (error) {
        console.error('Error fetching blog data:', error);
        return {
            props: {
                categories: [],
                posts: []
            },
            revalidate: 60, // Revalidate every minute
        };
    }
} 